<i18n locale="en">
{
  "labels": {
    "source": "Video source",
    "upload": "Upload",
    "video": "YouTube link",
    "youtube": "YouTube"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer :label="t('labels.source')">
      <OwnSegmentedControl
        :options="sourceOptions"
        :model-value="source"
        @update:model-value="changeSource"
      />
    </OwnInputContainer>
    <OwnInputContainer v-if="source === 'youtube'" :label="t('labels.video')">
      <YoutubeInput
        :model-value="youtubeId"
        @update:model-value="updateYoutube"
      />
    </OwnInputContainer>
    <OwnInputContainer v-else :label="t('labels.upload')">
      <HostedVideoInput
        :brand-id="brandId"
        :model-value="modelValue?.type === 'cdn' ? modelValue.params : null"
        :on-video-ready="onVideoReady"
        :upload-key="uploadKey"
      />
    </OwnInputContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import YoutubeInput from '@/components/inputs/YoutubeInput.vue'
import HostedVideoInput from '@/features/website-builder/builder/components/HostedVideoInput.vue'
import {
  type HostedVideoValue,
  stopHostedVideoPolling,
} from '@/features/website-builder/builder/components/hostedVideoPolling'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnSegmentedControl } from '@/ui/OwnSegmentedControl'

type ContentVideoValue =
  | { type: 'youtube'; params: { id: string } }
  | { type: 'cdn'; params: HostedVideoValue }

const props = defineProps<{
  brandId: string
  modelValue: ContentVideoValue
  onVideoReady: (value: HostedVideoValue) => Promise<void> | void
  uploadKey: string
}>()

const emit = defineEmits<{
  update: [
    payload: {
      controlName: 'video'
      status: { valid: boolean }
      value: ContentVideoValue
    },
  ]
}>()

const { t } = useI18n()
const source = ref<'youtube' | 'cdn'>(props.modelValue?.type ?? 'youtube')
const youtubeId = ref(
  props.modelValue?.type === 'youtube' ? props.modelValue.params.id : ''
)
const sourceOptions = computed(() =>
  (['youtube', 'cdn'] as const).map((value) => ({
    label: t(`labels.${value === 'cdn' ? 'upload' : value}`),
    value,
  }))
)

watch(
  () => props.modelValue,
  (value) => {
    source.value = value?.type ?? 'youtube'
    if (value?.type === 'youtube') youtubeId.value = value.params.id
  }
)

const updateYoutube = (id: string): void => {
  youtubeId.value = id
  emit('update', {
    controlName: 'video',
    status: { valid: Boolean(id) },
    value: { params: { id }, type: 'youtube' },
  })
}

const changeSource = async (value: 'youtube' | 'cdn'): Promise<void> => {
  source.value = value
  if (value === 'youtube') {
    await stopHostedVideoPolling(props.uploadKey)
    updateYoutube('')
  }
}
</script>
